import * as Card from '../card'
import { ReassignmentStatusBadge } from '../reassignment-status-badge'

/**
 * Props for the ReassignmentCaseAssignmentCard component.
 * Displays a card in the case assignment flow showing an approved reassignment request.
 * This is a presentational component - all state and formatting handled by consumer.
 */
interface ReassignmentCaseAssignmentCardProps {
  /** Current status of the reassignment (e.g., "assigned_to_cc", "approved") */
  status: string
  /** Name of the current clinician, or "None" */
  currentClinician: string
  /** Pre-formatted date string for when reassignment takes effect */
  effectiveDate: string
  /** Student availability details */
  studentAvailability: string
  /** Optional callback when "View Details" is clicked */
  onViewDetailsClick?: () => void
}

/**
 * ReassignmentCaseAssignmentCard
 *
 * Purely presentational card component for displaying reassignment information
 * in the case assignment flow. Uses horizontal layout with data columns.
 *
 * Unlike the base/scheduled/in-progress cards (which use vertical sand background),
 * this card has a horizontal layout with white background and border, matching
 * the case assignment design.
 */
export function ReassignmentCaseAssignmentCard({
  status,
  currentClinician,
  effectiveDate,
  studentAvailability,
  onViewDetailsClick,
}: ReassignmentCaseAssignmentCardProps) {
  return (
    <Card.Root className="px-6 py-4 border border-gray-stroke">
      <div className="flex gap-12 items-center">
        <div className="flex items-center gap-6">
          <i className="fa-solid fa-arrows-turn-to-dots text-orange" />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-lg">Reassignment Request</span>
              <ReassignmentStatusBadge status={status} />
            </div>
            {onViewDetailsClick && (
              <button
                type="button"
                onClick={onViewDetailsClick}
                className="underline text-indigo text-md text-left"
              >
                View Details
              </button>
            )}
          </div>
        </div>

        <div className="w-[1px] self-stretch bg-gray-stroke" />

        <div className="flex flex-col">
          <span className="text-charcoal-50 text-sm">Current Clinician</span>
          <span className="text-charcoal-75">{currentClinician}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-charcoal-50 text-sm">Effective Date</span>
          <span className="text-charcoal-75">{effectiveDate}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-charcoal-50 text-sm">Student Availability</span>
          <span className="text-charcoal-75">{studentAvailability}</span>
        </div>
      </div>
    </Card.Root>
  )
}
